import type React from 'react';
import { createContext, useContext, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { createPageStore, type PageStoreInstance } from '@/stores/factories/pageStoreFactory';
import { createUIStore, type UIStoreInstance } from '@/stores/factories/uiStoreFactory';
import { createChatStore, type ChatStoreInstance } from '@/stores/factories/chatStoreFactory';
import { createCanvasPreviewStore, type CanvasPreviewStoreInstance } from '@/stores/factories/canvasPreviewStoreFactory';
import type { CanvasPreviewStore } from '@/stores/types';
import { createNotificationService } from '@/services/notificationService';
import { createConversationUiStore, type ConversationUiStoreInstance, type ConversationUiState } from '@/stores/factories/conversationUiStoreFactory';

interface StoreContextValue {
  pageStore: PageStoreInstance;
  uiStore: UIStoreInstance;
  chatStore: ChatStoreInstance;
  canvasPreviewStore: CanvasPreviewStoreInstance;
  conversationUiStore: ConversationUiStoreInstance;
}

export const StoreContext = createContext<StoreContextValue | undefined>(undefined);

interface StoreProviderProps {
  children: React.ReactNode;
  initialState?: {
    pageStore?: Parameters<typeof createPageStore>[0];
    uiStore?: Parameters<typeof createUIStore>[1];
    chatStore?: Parameters<typeof createChatStore>[0];
    canvasPreviewStore?: Parameters<typeof createCanvasPreviewStore>[0];
    conversationUiStore?: Parameters<typeof createConversationUiStore>[0];
  };
  services?: {
    notificationService?: Parameters<typeof createUIStore>[0]['notificationService'];
  };
}

export function StoreProvider({ 
  children, 
  initialState = {},
  services = {}
}: StoreProviderProps) {
  const storesRef = useRef<StoreContextValue | undefined>(undefined);

  if (!storesRef.current) {
    const notificationService = services.notificationService || createNotificationService();

    storesRef.current = {
      pageStore: createPageStore(initialState.pageStore),
      uiStore: createUIStore(
        { notificationService },
        initialState.uiStore
      ),
      chatStore: createChatStore(initialState.chatStore),
      canvasPreviewStore: createCanvasPreviewStore(initialState.canvasPreviewStore),
      conversationUiStore: createConversationUiStore(initialState.conversationUiStore),
    };
  }

  return (
    <StoreContext.Provider value={storesRef.current}>
      {children}
    </StoreContext.Provider>
  );
}

// ベースフック
// raw store instance (valtio proxy) へアクセスしたい呼び出し側のため export する。
// 例: view 奪取ガードの userNavToken をリアクティブ購読せず raw 読みする getter
// (`useStore().conversationUiStore.store.userNavToken`)。
export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within StoreProvider');
  }
  return context;
}

// 個別のストアフック
export function usePageStore() {
  const { pageStore } = useStore();
  const snapshot = useSnapshot(pageStore.store);
  return {
    state: snapshot,
    actions: pageStore.actions,
    derived: pageStore.derived,
  };
}

export function useUIStore() {
  const { uiStore } = useStore();
  const snapshot = useSnapshot(uiStore.store);
  return {
    state: snapshot,
    actions: uiStore.actions,
  };
}

export function useChatStore() {
  const { chatStore } = useStore();
  const snapshot = useSnapshot(chatStore.store);
  return {
    state: snapshot,
    actions: chatStore.actions,
  };
}

export function useCanvasPreviewStore() {
  const { canvasPreviewStore } = useStore();
  const snapshot = useSnapshot(canvasPreviewStore.store) as CanvasPreviewStore;
  return {
    state: snapshot,
    actions: canvasPreviewStore.actions,
  };
}

export function useConversationUIStore() {
  const { conversationUiStore } = useStore();
  const snapshot = useSnapshot(conversationUiStore.store) as ConversationUiState;
  return {
    state: snapshot,
    actions: conversationUiStore.actions,
  };
}

// 後方互換性のためのフック
export function useStores() {
  const stores = useStore();
  const pageSnapshot = useSnapshot(stores.pageStore.store);
  const uiSnapshot = useSnapshot(stores.uiStore.store);
  const chatSnapshot = useSnapshot(stores.chatStore.store);
  const canvasPreviewSnapshot = useSnapshot(stores.canvasPreviewStore.store);
  const conversationUiSnapshot = useSnapshot(stores.conversationUiStore.store);

  return {
    pageStore: {
      ...pageSnapshot,
      ...stores.pageStore.actions,
      derived: stores.pageStore.derived,
    },
    uiStore: {
      ...uiSnapshot,
      ...stores.uiStore.actions,
    },
    chatStore: {
      ...chatSnapshot,
      ...stores.chatStore.actions,
    },
    canvasPreviewStore: {
      ...canvasPreviewSnapshot,
      ...stores.canvasPreviewStore.actions,
    },
    conversationUiStore: {
      ...conversationUiSnapshot,
      ...stores.conversationUiStore.actions,
    },
  };
}
